import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { OmrAmount } from '../../../main/components/OmrAmount';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import {
  cancelInvoiceCheckout,
  createInvoiceCheckout,
  payFullInvoice,
  setInvoiceWhatsAppCheckout,
} from '../../../main/API/invoices';
import {
  getCheckoutApiErrorMessage,
  getCheckoutRemainingCapacity,
  getCheckoutTitle,
} from '../../../main/utils/invoiceCheckouts';
import { getPaymentLinkExpirationStatus } from '../../../main/services/format';
import CopyPaymentLinkButton from './CopyPaymentLinkButton';
import { getDocLocale } from '../../../shipments_manager/views/htmlDocumentUtils';

function checkoutStatusColor(status) {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'paid':
      return 'success';
    case 'cancelled':
      return 'neutral';
    case 'expired':
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * Staff invoice detail: list / create / cancel / pay-full payment links (InvoiceCheckout).
 * Does not treat checkouts as ledger payments.
 */
export default function InvoicePaymentLinksSection({ invoice, onInvoiceRefresh }) {
  const { t, i18n } = useTranslation('ui');
  const locale = getDocLocale(i18n.language);
  const [createOpen, setCreateOpen] = useState(false);
  const [createAmount, setCreateAmount] = useState('');
  const [createNote, setCreateNote] = useState('');
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [payFullOpen, setPayFullOpen] = useState(false);
  const [payFullLoading, setPayFullLoading] = useState(false);

  const [whatsappLoadingId, setWhatsappLoadingId] = useState(null);

  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const checkouts = useMemo(
    () => (invoice && Array.isArray(invoice.payment_checkouts) ? invoice.payment_checkouts : []),
    [invoice]
  );
  const maxNewAmount = useMemo(() => getCheckoutRemainingCapacity(invoice), [invoice]);
  const amountDue = Number(invoice && invoice.amount_due != null ? invoice.amount_due : 0);
  const amountPaid = invoice && invoice.amount_paid != null ? invoice.amount_paid : null;
  const canPayFull = amountDue > 0;

  const refresh = async () => {
    if (typeof onInvoiceRefresh === 'function') {
      await onInvoiceRefresh();
    }
  };

  const openCreateModal = () => {
    setCreateAmount(maxNewAmount > 0 ? String(maxNewAmount.toFixed(3)) : '');
    setCreateNote('');
    setCreateError('');
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setCreateError('');
    const amountNum = Number(createAmount);
    if (!(amountNum > 0)) {
      setCreateError(t('invoiceForm.checkoutAmountRequired'));
      return;
    }
    if (amountNum > maxNewAmount + 1e-9) {
      setCreateError(t('invoiceForm.checkoutAmountExceedsCapacity', { max: maxNewAmount.toFixed(3) }));
      return;
    }
    setCreateLoading(true);
    try {
      const response = await createInvoiceCheckout(invoice.id, {
        amount: Number(amountNum).toFixed(3),
        note: createNote,
      });
      const link =
        response && response.data && response.data.checkout && response.data.checkout.payment_link;
      setCreateOpen(false);
      setActionSuccess(
        link
          ? t('invoiceForm.checkoutCreatedWithLink')
          : t('invoiceForm.checkoutCreated')
      );
      await refresh();
      if (link) {
        try {
          await navigator.clipboard.writeText(link);
        } catch (_) {
          /* clipboard optional */
        }
      }
    } catch (err) {
      setCreateError(
        getCheckoutApiErrorMessage(err, t('invoiceForm.checkoutCreateFailed'))
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget || !invoice) return;
    setCancelLoading(true);
    setActionError('');
    try {
      await cancelInvoiceCheckout(invoice.id, cancelTarget.id);
      setCancelTarget(null);
      setActionSuccess(t('invoiceForm.checkoutCancelled'));
      await refresh();
    } catch (err) {
      setActionError(getCheckoutApiErrorMessage(err, t('invoiceForm.checkoutCancelFailed')));
      setCancelTarget(null);
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePayFull = async () => {
    if (!invoice) return;
    setPayFullLoading(true);
    setActionError('');
    try {
      const response = await payFullInvoice(invoice.id);
      const link =
        (response && response.data && response.data.checkout && response.data.checkout.payment_link)
        || (response && response.data && response.data.payment_link);
      setPayFullOpen(false);
      setActionSuccess(t('invoiceForm.payFullSuccess'));
      await refresh();
      if (link) {
        try {
          await navigator.clipboard.writeText(link);
        } catch (_) {
          /* clipboard optional */
        }
      }
    } catch (err) {
      setActionError(getCheckoutApiErrorMessage(err, t('invoiceForm.payFullFailed')));
      setPayFullOpen(false);
    } finally {
      setPayFullLoading(false);
    }
  };

  const handleSetWhatsAppLink = async (checkoutId) => {
    if (!invoice) return;
    setWhatsappLoadingId(checkoutId);
    setActionError('');
    setActionSuccess('');
    try {
      await setInvoiceWhatsAppCheckout(invoice.id, checkoutId);
      setActionSuccess(t('invoiceForm.whatsappLinkSet'));
      await refresh();
    } catch (err) {
      setActionError(getCheckoutApiErrorMessage(err, t('invoiceForm.whatsappLinkSetFailed')));
    } finally {
      setWhatsappLoadingId(null);
    }
  };

  if (!invoice) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography level="h4" sx={{ mb: 1 }}>{t('invoiceForm.sectionPaymentLinks')}</Typography>
      <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600', maxWidth: 900 }}>
        {t('invoiceForm.sectionPaymentLinksIntro')}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <Box>
          <FormLabel>{t('invoiceForm.amountPaid')}</FormLabel>
          <Typography level="title-md">
            {amountPaid != null ? <OmrAmount value={amountPaid} /> : '—'}
          </Typography>
        </Box>
        <Box>
          <FormLabel>{t('invoiceForm.amountDue')}</FormLabel>
          <Typography level="title-md">
            {invoice.amount_due != null ? <OmrAmount value={invoice.amount_due} /> : '—'}
          </Typography>
        </Box>
      </Stack>

      {actionError ? (
        <Alert color="danger" variant="soft" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      ) : null}
      {actionSuccess ? (
        <Alert color="success" variant="soft" sx={{ mb: 2 }} onClose={() => setActionSuccess('')}>
          {actionSuccess}
        </Alert>
      ) : null}

      <PermissionGate permission={PERMISSIONS.INVOICES_UPDATE}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <Button
            size="sm"
            variant="soft"
            onClick={openCreateModal}
            disabled={maxNewAmount <= 0}
          >
            {t('invoiceForm.addPaymentLink')}
          </Button>
          {canPayFull ? (
            <Button size="sm" variant="outlined" color="primary" onClick={() => setPayFullOpen(true)}>
              {t('invoiceForm.payFullRemaining')}
            </Button>
          ) : null}
        </Stack>
      </PermissionGate>

      {checkouts.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          {t('invoiceForm.noCheckouts')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {checkouts.map((checkout) => {
            const isPending = checkout.status === 'pending' && !!checkout.payment_link;
            const expireLabel = (() => {
              if (!checkout.payment_link_expire_at && !checkout.payment_link) return '—';
              if (!checkout.payment_link) return '—';
              const status = getPaymentLinkExpirationStatus(
                checkout.payment_link_expire_at,
                checkout.payment_link
              );
              if (status.status === 'unlimited') return t('invoiceForm.unlimitedPaymentLink');
              if (status.status === 'expired') return t('invoiceForm.paymentLinkExpiredShort');
              return status.remainingTime || '—';
            })();
            return (
              <Box
                key={checkout.id}
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'neutral.outlinedBorder',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160 }}>
                  <Typography level="title-sm">
                    {getCheckoutTitle(checkout, locale, t)}
                  </Typography>
                  <Typography level="title-sm">
                    <OmrAmount value={checkout.amount} />
                  </Typography>
                  {checkout.note && checkout.kind !== 'custom' ? (
                    <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                      {checkout.note}
                    </Typography>
                  ) : null}
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="sm" variant="soft" color="primary">
                      {t(`invoiceForm.checkoutKind.${checkout.kind || 'shared'}`, {
                        defaultValue: checkout.kind || 'shared',
                      })}
                    </Chip>
                    <Chip size="sm" variant="soft" color={checkoutStatusColor(checkout.status)}>
                      {t(`invoiceForm.checkoutStatus.${checkout.status}`, {
                        defaultValue: checkout.status,
                      })}
                    </Chip>
                    {checkout.needs_manual_refund ? (
                      <Chip size="sm" variant="soft" color="danger">
                        {t('invoiceForm.needsManualRefund')}
                      </Chip>
                    ) : null}
                    {checkout.is_whatsapp_link ? (
                      <Chip size="sm" variant="soft" color="success">
                        {t('invoiceForm.whatsappLinkBadge')}
                      </Chip>
                    ) : null}
                  </Stack>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {t('invoiceForm.checkoutExpires')}: {expireLabel}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {isPending ? (
                    <>
                      <Tooltip title={t('invoiceForm.openPaymentLink')}>
                        <IconButton
                          size="sm"
                          variant="soft"
                          component="a"
                          href={checkout.payment_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <CopyPaymentLinkButton
                        paymentLink={checkout.payment_link}
                        tooltip={t('invoiceForm.copyPaymentLink')}
                      />
                      <PermissionGate permission={PERMISSIONS.INVOICES_UPDATE}>
                        <Button
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => setCancelTarget(checkout)}
                        >
                          {t('invoiceForm.cancelCheckout')}
                        </Button>
                      </PermissionGate>
                      {!checkout.is_whatsapp_link ? (
                        <PermissionGate permission={PERMISSIONS.INVOICES_UPDATE}>
                          <Button
                            size="sm"
                            variant="soft"
                            color="success"
                            loading={whatsappLoadingId === checkout.id}
                            disabled={whatsappLoadingId != null}
                            onClick={() => handleSetWhatsAppLink(checkout.id)}
                            startDecorator={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                          >
                            {t('invoiceForm.setWhatsAppLink')}
                          </Button>
                        </PermissionGate>
                      ) : null}
                    </>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Create custom-amount checkout */}
      <Modal open={createOpen} onClose={() => !createLoading && setCreateOpen(false)}>
        <ModalDialog variant="outlined" sx={{ maxWidth: 420, width: '100%' }}>
          <DialogTitle>{t('invoiceForm.addPaymentLink')}</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 1.5, color: 'neutral.600' }}>
              {t('invoiceForm.checkoutCapacityHint', { max: maxNewAmount.toFixed(3) })}
            </Typography>
            {createError ? (
              <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>{createError}</Alert>
            ) : null}
            <FormLabel>{t('invoiceForm.checkoutAmount')}</FormLabel>
            <Input
              type="number"
              value={createAmount}
              onChange={(e) => setCreateAmount(e.target.value)}
              slotProps={{ input: { min: 0, step: '0.001', max: maxNewAmount } }}
            />
            <Box sx={{ mt: 1.5 }}>
              <FormLabel>{t('invoiceForm.checkoutNote')}</FormLabel>
              <Input
                type="text"
                value={createNote}
                onChange={(e) => setCreateNote(e.target.value)}
                placeholder={t('invoiceForm.checkoutNotePlaceholder')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" disabled={createLoading} onClick={() => setCreateOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              loading={createLoading}
              onClick={handleCreate}
              disabled={!(Number(createAmount) > 0) || Number(createAmount) > maxNewAmount + 1e-9}
            >
              {t('invoiceForm.createCheckout')}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Cancel pending checkout */}
      <Modal open={!!cancelTarget} onClose={() => !cancelLoading && setCancelTarget(null)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon />
            {t('invoiceForm.cancelCheckoutTitle')}
          </DialogTitle>
          <DialogContent>
            {t('invoiceForm.cancelCheckoutBody')}
          </DialogContent>
          <DialogActions>
            <Button variant="plain" disabled={cancelLoading} onClick={() => setCancelTarget(null)}>
              {t('actions.cancel')}
            </Button>
            <Button color="danger" loading={cancelLoading} onClick={handleCancel}>
              {t('invoiceForm.cancelCheckoutConfirm')}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Pay full remaining */}
      <Modal open={payFullOpen} onClose={() => !payFullLoading && setPayFullOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon />
            {t('invoiceForm.payFullRemaining')}
          </DialogTitle>
          <DialogContent>
            {t('invoiceForm.payFullConfirmBody')}
          </DialogContent>
          <DialogActions>
            <Button variant="plain" disabled={payFullLoading} onClick={() => setPayFullOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button loading={payFullLoading} onClick={handlePayFull}>
              {t('invoiceForm.payFullConfirm')}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
